import { useState } from 'react';

interface Benefit {
  title: string;
  condition: string;
  badge?: string;
}

interface VendorBenefits {
  vendorName: string;
  vendorCategory: string;
  benefits: Benefit[];
}



interface PackageBenefitsSectionProps {
  data: VendorBenefits[];
}

export default function PackageBenefitsSection({ data }: PackageBenefitsSectionProps) {
  const [expanded, setExpanded] = useState(false);

  // Flatten all benefits with vendor context for preview
  const flatBenefits = data?.flatMap((vendor) =>
    vendor.benefits.map((b) => ({
      ...b,
      vendorName: vendor.vendorName,
      vendorCategory: vendor.vendorCategory,
    }))
  );
  const previewItems = flatBenefits?.slice(0, 3);
  const totalCount = flatBenefits?.length;

  return (
    <div id="promotions" className="pt-6">
      {/* Title */}
      <div className="flex items-end gap-2 mb-1">
        <h2 className="text-lg md:text-xl font-bold text-[#121B35]">프로모션</h2>
      </div>
      <p className="text-sm md:text-base text-[#536079] mb-4">
        제휴 패키지 이용 시 적용 가능한 할인과 추가 서비스를 확인해보세요.
      </p>

      {/* Card */}
      <div className="bg-white rounded-2xl">
        {/* ─── Collapsed: preview 3 items only ─── */}
        {!expanded && (
          <div className="p-4 lg:p-5">
            <div className="space-y-0">
              {previewItems?.map((item, i) => (
                <div
                  key={i}
                  className="flex gap-3 items-start py-3 border-b border-[#E8E2DA]/50 last:border-0"
                >
                  <span className="shrink-0 text-[10px] md:text-xs text-[#B9965B] font-medium bg-[#F7F2EA] px-2 py-0.5 rounded mt-0.5">
                    {item.vendorCategory}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm md:text-base font-semibold text-[#121B35] leading-snug">
                      {item.title}
                    </p>
                    <p className="text-xs text-[#536079] mt-0.5 leading-relaxed">
                      {item.vendorName} · {item.condition}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─── Expanded: grouped by vendor ─── */}
        {expanded && (
          <div className="p-4 lg:p-5">
            <div className="space-y-5">
              {data?.map((vendor) => (
                <div key={vendor.vendorName}>
                  {/* Group header */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-semibold text-[#121B35]">
                      {vendor.vendorName}
                    </span>
                    <span className="text-[10px] text-[#536079] bg-[#F7F2EA] px-2 py-0.5 rounded-full">
                      {vendor.vendorCategory}
                    </span>
                  </div>
                  {/* Benefits list */}
                  <div className="lg:grid lg:grid-cols-2 lg:gap-x-6">
                    {vendor.benefits.map((b, idx) => (
                      <div
                        key={idx}
                        className="flex gap-3 py-2 border-b border-[#E8E2DA]/30 last:border-0 lg:last:border-0"
                      >
                        <span className="w-1 h-1 rounded-full bg-[#B9965B] mt-1.5 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-[#121B35] leading-snug">
                            {b.title}
                          </p>
                          <p className="text-[11px] md:text-xs text-[#536079] mt-0.5 leading-relaxed">
                            {b.condition}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Toggle button */}
        <div className="px-4 lg:px-5 pb-4 lg:pb-5">
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs md:text-sm text-[#536079] hover:text-[#121B35] transition-colors flex items-center gap-1 whitespace-nowrap"
          >
            {expanded ? '혜택 접기' : `전체 혜택 ${totalCount}개 보기`}
            <i className={expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
          </button>
        </div>
      </div>

      {/* Bottom note */}
      <p className="text-[11px] md:text-xs text-[#536079] mt-3 px-1 leading-relaxed">
        혜택은 이벤트 기간, 방문 일정, 지정 예약 여부에 따라 달라질 수 있으며 상담 시 최종 확인이 필요합니다.
      </p>
    </div>
  );
}